import { z } from "zod";
import type { ChainProvider } from "./provider";
import {
  GraduationEntry,
  Launch,
  RiskEvidence,
  type GraduationEntry as GraduationEntryType,
} from "../schemas";
import type { Chain } from "../schemas";

const POLL_MS = 4_000;

const HostedListingWire = z.object({
  tokenAddress: z.string().regex(/^0x[0-9a-f]{40}$/),
  quoteAddress: z
    .string()
    .regex(/^0x[0-9a-f]{40}$/)
    .optional(),
  poolAddress: z.string().regex(/^0x[0-9a-f]{40}$/),
  factoryVersion: z.string(),
  originator: z.string().regex(/^0x[0-9a-f]{40}$/),
  blockNumber: z.number().int().nonnegative(),
  timestamp: z.number().nonnegative(),
  txHash: z.string().regex(/^0x[0-9a-f]{64}$/),
  logIndex: z.number().int().nonnegative(),
  listingStatus: z.literal("verified"),
  marketDataStatus: z.enum(["verified", "degraded", "unavailable"]),
  forensicsStatus: z.enum(["verified", "degraded", "unavailable"]),
  lifecycle: z.literal("direct-pool"),
  gatewayActivityStatus: z.enum(["verified", "degraded", "unavailable"]),
  ticker: z.string().min(1).max(64).optional(),
  name: z.string().min(1).max(128).optional(),
  platformLabel: z.string().min(1).max(64).optional(),
  poolId: z
    .string()
    .regex(/^0x[0-9a-f]{64}$/)
    .optional(),
  riskEvidence: RiskEvidence.optional(),
});

const HostedSnapshot = z.object({
  schemaVersion: z.literal("flurry-hosted/v1"),
  chain: z.enum(["avalanche", "robinhood"]),
  state: z.enum(["fresh", "stale", "degraded", "unavailable"]),
  listings: z.array(HostedListingWire).max(500),
});

function tickerFor(address: string): string {
  return address.slice(2, 8).toUpperCase();
}

function hostedListingsUrl(chain: Chain): string {
  const api = new URL(`${import.meta.env.BASE_URL}api/`, window.location.origin);
  return new URL(`v1/chains/${chain}/listings`, api).toString();
}

export function createHostedChainProvider(
  chain: Chain,
  callbacks: {
    onStatus?: (status: "LIVE" | "STALE" | "DEGRADED" | "UNAVAILABLE") => void;
    onThrottle?: (value: boolean) => void;
  } = {},
): ChainProvider {
  const listeners = new Set<(launch: Launch) => void>();
  const seen = new Set<string>();
  let entries: GraduationEntryType[] = [];
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const poll = async () => {
    try {
      const response = await fetch(hostedListingsUrl(chain), {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(`hosted data HTTP ${response.status}`);
      const snapshot = HostedSnapshot.parse(await response.json());
      if (snapshot.chain !== chain) throw new Error("hosted data chain mismatch");
      callbacks.onStatus?.(
        snapshot.state === "fresh"
          ? "LIVE"
          : (snapshot.state.toUpperCase() as "STALE" | "DEGRADED" | "UNAVAILABLE"),
      );
      callbacks.onThrottle?.(snapshot.state === "degraded");
      entries = snapshot.listings.map((item) =>
        GraduationEntry.parse({
          chain,
          mint: item.tokenAddress,
          ticker: item.ticker ?? tickerFor(item.tokenAddress),
          program: chain === "avalanche" ? "AVALANCHE_LFJ" : "POOLS_TRADE",
          platformLabel:
            item.platformLabel ??
            (chain === "avalanche" ? `LFJ ${item.factoryVersion}` : "POOLS.TRADE"),
          curveProgressPct: 100,
          mcapUsd: 0,
          vol1hUsd: 0,
          holders: 0,
          volHoldersVerified: false,
          pinned: false,
          lifecycle: "direct-pool",
          listingStatus: item.listingStatus,
          marketDataStatus: item.marketDataStatus,
          riskEvidence: item.riskEvidence,
        }),
      );

      for (const item of snapshot.listings.slice().reverse()) {
        const key = `${item.txHash}:${item.logIndex}:${item.tokenAddress}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const ticker = item.ticker ?? tickerFor(item.tokenAddress);
        const launch = Launch.parse({
          chain,
          mint: item.tokenAddress,
          ticker,
          name:
            item.name ??
            (chain === "avalanche" ? `LFJ-listed token ${ticker}` : `Pools.trade token ${ticker}`),
          program: chain === "avalanche" ? "AVALANCHE_LFJ" : "POOLS_TRADE",
          platformLabel:
            item.platformLabel ??
            (chain === "avalanche" ? `LFJ ${item.factoryVersion}` : "POOLS.TRADE"),
          deployer: item.originator,
          deploySlot: item.blockNumber,
          launchedAt: item.timestamp,
          mcapUsd: 0,
          devHoldsPct: 0,
          deployerPriorLaunches: 0,
          deployerPriorRugs: 0,
          rugHistoryVerified: false,
          slotActivity: [],
          lifecycle: item.lifecycle,
          listingStatus: item.listingStatus,
          marketDataStatus: item.marketDataStatus,
          forensicsStatus: item.forensicsStatus,
          poolAddress: item.poolAddress,
          proofTxHash: item.txHash,
          riskEvidence: item.riskEvidence,
        });
        for (const listener of listeners) listener(launch);
      }
    } catch {
      callbacks.onStatus?.("UNAVAILABLE");
    } finally {
      if (!disposed) timer = setTimeout(poll, POLL_MS);
    }
  };
  void poll();

  return {
    id: `hosted:${chain}`,
    chain,
    name: `${chain} hosted listing index`,
    capabilities: {
      lifecycle: "direct-pool",
      forensics: "unavailable",
      marketData: "unavailable",
    },
    subscribeLaunches(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async getGraduationCandidates() {
      return entries;
    },
    async resolveQueuedMint(mint) {
      const found = entries.find((entry) => entry.mint.toLowerCase() === mint.toLowerCase());
      if (!found) throw new Error("token is not present in the hosted verified listing index");
      return found;
    },
    dispose() {
      disposed = true;
      if (timer) clearTimeout(timer);
    },
  };
}
